import { useRef, useEffect } from 'react';
import { Box, Typography, Container, Paper } from '@mui/material';
import { motion } from 'framer-motion';
import AboutLogosRow from '../../components/pages/about/AboutLogosRow';
import { SEO } from '../../components';
import { octopusSyncContent } from '../../data/content/octopusSync';
import CustomComparisonTable from '../../components/pages/octopussync/CustomComparisonTable';
import ModernList from '../../components/ModernList';
import PageTitle from '../../components/common/PageTitle';
import THEME from '../../constants/theme';

const OctopusSyncPage = ({ setRefs }) => {
  const octopusSyncRef = useRef(null);
  const aboutRef = useRef(null);
  const featuresRef = useRef(null);
  const comparisonRef = useRef(null);

  useEffect(() => {
    setRefs({ 
      octopussync: octopusSyncRef,
      about: aboutRef,
      features: featuresRef,
      comparison: comparisonRef
    });
  }, [setRefs]);

  return (
    <>
      <SEO
        title="OctopusSync | Multi-Venue Copy Trading and Trade Synchronisation"
        description="Built for retail speed and institutional connectivity with cross-technology copy trading and FIX 4.4 support."
        keywords="octopus, copy trading, FIX 4.4, institutional connectivity, synchronisation"
        canonical="/octopussync"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "OctopusSync",
          "url": "https://octopus-sync.com",
        }}
      />

      <Box ref={octopusSyncRef} id="octopussync" sx={{ minHeight: '100vh', pt: 8, position: 'relative' }}>
        <Container maxWidth="xl" sx={{ py: { xs: 4, md: 6 } }}>
          <PageTitle 
            title={octopusSyncContent.hero.title}
            subtitle="Multi-Venue Copy Trading and Trade Synchronisation - Built for Retail Speed and Institutional Connectivity"
          />
          <Typography variant="body1" sx={{ mb: 5, maxWidth: 1000, lineHeight: 1.8 }}>
            {octopusSyncContent.hero.description}
          </Typography>

          {/* About */}
          <Box ref={aboutRef} id="about" sx={{ py: { xs: 4, md: 6 } }}>
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <Paper elevation={6} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.6)', background: 'linear-gradient(135deg, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.72) 100%)' }}>
                <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
                  {octopusSyncContent.about.title}
                </Typography>
                {octopusSyncContent.about.content.map((p, idx) => (
                  <Typography key={idx} variant="body1" sx={{ mb: idx === octopusSyncContent.about.content.length - 1 ? 0 : 2, lineHeight: 1.8 }}>
                    {p}
                  </Typography>
                ))}
              </Paper>
            </motion.div>
            <AboutLogosRow />
          </Box>

          {/* Features */}
          <Box ref={featuresRef} id="features" sx={{ py: { xs: 4, md: 6 } }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, color: THEME.COLORS.PRIMARY_PURPLE }}>
              {octopusSyncContent.features.title}
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8 }}>
              {octopusSyncContent.features.intro}
            </Typography>
            <ModernList
              items={octopusSyncContent.features.items.map(it => ({ text: it.title, children: [it.description] }))}
              spacing={2}
            />
          </Box>

          {/* Comparison (Octopus only) */}
          <Box ref={comparisonRef} id="comparison" sx={{ py: { xs: 4, md: 6 } }}>
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 800, color: 'primary.main' }}>
              Comparison
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7, maxWidth: 720 }}>
              A quick look at how OctopusSync stacks up against typical providers across connectivity, support, and platform depth.
            </Typography>
            <CustomComparisonTable />
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default OctopusSyncPage;
